import { PhoneOffIcon, MicIcon, MicOffIcon, VideoIcon, VideoOffIcon, Volume2Icon, CrownIcon, MonitorUp } from "lucide-react";
import type { MediaSession } from "@/lib/av-stream-types";
import { useEffect, useRef, useState } from "react";
import { getPeerAvatarColor, getPeerBannerColor, renderPeerAvatarEmoji, type RemotePeerProfile } from "@/lib/peer-profile-utils";
import { useUserPreferences } from "@/hooks/useUserPreferences";

interface StreamWindowProps {
  session: MediaSession;
  peerAddress: string;
  contactName: string;
  contactInitials: string;
  contactProfile?: RemotePeerProfile;
  isLocal?: boolean;
  isGroupInitiator?: boolean;
  onEndCall?: () => void;
  onToggleVideo?: (enable: boolean) => Promise<boolean>;
  onToggleScreenShare?: (enable: boolean) => Promise<boolean>;
}

/**
 * Video/Audio stream window component
 * Shows video feed or avatar with initials for audio-only calls
 */
export function StreamWindow({
  session,
  peerAddress,
  contactName,
  contactInitials,
  contactProfile,
  isLocal = false,
  isGroupInitiator = false,
  onEndCall,
  onToggleVideo,
  onToggleScreenShare
}: StreamWindowProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [remoteStreamUpdate, setRemoteStreamUpdate] = useState(0);
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isSpeakerMuted, setIsSpeakerMuted] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(session.videoEnabled || false);
  const [isTogglingVideo, setIsTogglingVideo] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(session.screenShareEnabled || false);
  const [isTogglingScreenShare, setIsTogglingScreenShare] = useState(false);
  const [frequencyData, setFrequencyData] = useState<number[]>(new Array(8).fill(0));
  const [remoteVideoEnabled, setRemoteVideoEnabled] = useState<boolean>(
    session.remoteVideoEnabled?.get(peerAddress) ?? 
    (session.remoteStreams.get(peerAddress)?.getVideoTracks().length ?? 0) > 0
  );
  const [isReconnecting, setIsReconnecting] = useState(session.state === 'connecting');
  const [remoteAudioActive, setRemoteAudioActive] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const remoteAudioCtxRef = useRef<AudioContext | null>(null);
  const remoteAnalyserRef = useRef<AnalyserNode | null>(null);
  const remoteAnimFrameRef = useRef<number | null>(null);
  const userPreferences = useUserPreferences();
  
  // Listen for session disconnection/reconnection events
  useEffect(() => {
    const handleSessionDisconnected = (event: CustomEvent) => {
      if (event.detail.sessionId === session.sessionId) {
        console.log('[STREAM_WINDOW] Session disconnected, showing reconnecting state', {
          sessionId: session.sessionId.slice(0, 8),
          peer: event.detail.peer?.slice(0, 8)
        });
        setIsReconnecting(true);
      }
    };
    
    const handleSessionReconnected = (event: CustomEvent) => {
      if (event.detail.sessionId === session.sessionId) {
        console.log('[STREAM_WINDOW] Session reconnected', {
          sessionId: session.sessionId.slice(0, 8),
          peer: event.detail.peer?.slice(0, 8)
        });
        setIsReconnecting(false);
        // Force re-attachment of streams
        setRemoteStreamUpdate(prev => prev + 1);
      }
    };
    
    window.addEventListener('av-session-disconnected', handleSessionDisconnected as EventListener);
    window.addEventListener('av-session-reconnected', handleSessionReconnected as EventListener);
    
    return () => {
      window.removeEventListener('av-session-disconnected', handleSessionDisconnected as EventListener);
      window.removeEventListener('av-session-reconnected', handleSessionReconnected as EventListener);
    };
  }, [session.sessionId]);
  
  // Listen for remote video toggle events
  useEffect(() => {
    if (isLocal) return;
    
    const handleRemoteVideoToggled = (event: CustomEvent) => {
      if (event.detail.peerAddress === peerAddress && event.detail.sessionId === session.sessionId) {
        console.log('[STREAM_WINDOW] Remote video toggle received', {
          peer: peerAddress.slice(0, 8),
          videoEnabled: event.detail.videoEnabled
        });
        setRemoteVideoEnabled(event.detail.videoEnabled);
      }
    };
    
    window.addEventListener('av-remote-video-toggled', handleRemoteVideoToggled as EventListener);
    return () => {
      window.removeEventListener('av-remote-video-toggled', handleRemoteVideoToggled as EventListener);
    };
  }, [peerAddress, session.sessionId, isLocal]);
  
  // Listen for remote stream additions (for this peer)
  useEffect(() => {
    const handleRemoteStreamAdded = (event: CustomEvent) => {
      if (event.detail.peerAddress === peerAddress && !isLocal) {
        console.log('[STREAM_WINDOW] Remote stream added event received', {
          peer: peerAddress.slice(0, 8),
          sessionId: event.detail.sessionId.slice(0, 8)
        });
        // Force re-attachment by triggering state update
        setRemoteStreamUpdate(prev => prev + 1);
      }
    };
    
    window.addEventListener('av-remote-stream-added', handleRemoteStreamAdded as EventListener);
    return () => {
      window.removeEventListener('av-remote-stream-added', handleRemoteStreamAdded as EventListener);
    };
  }, [peerAddress, isLocal]);
  
  // AV FIX B: Monitor remote track health.
  // After peer recreation, old tracks end while new tracks arrive on the same MediaStream.
  // After ICE blip, the video element may pause without an ended track.
  // In both cases, bump remoteStreamUpdate to trigger re-attachment.
  useEffect(() => {
    if (isLocal) return;
    const remoteStream = session.remoteStreams.get(peerAddress);
    if (!remoteStream) return;

    const handleTrackEnded = (e: Event) => {
      const track = e.target as MediaStreamTrack;
      console.log('[STREAM_WINDOW] Remote track ended — forcing re-attach', {
        peer: peerAddress.slice(0, 8),
        kind: track.kind,
        id: track.id
      });
      setRemoteStreamUpdate(prev => prev + 1);
    };

    const tracks = remoteStream.getTracks();
    tracks.forEach(t => t.addEventListener('ended', handleTrackEnded));

    return () => {
      tracks.forEach(t => t.removeEventListener('ended', handleTrackEnded));
    };
  }, [peerAddress, isLocal, session.remoteStreams, remoteStreamUpdate]);

  // Attach media stream to video element
  useEffect(() => {
    if (!videoRef.current) return;
    
    const videoElement = videoRef.current;
    
    if (isLocal && session.localStream) {
      videoElement.srcObject = session.localStream;
      
      console.log('[STREAM_WINDOW] Attached local video stream', {
        trackCount: session.localStream.getTracks().length,
        videoTracks: session.localStream.getVideoTracks().length,
        audioTracks: session.localStream.getAudioTracks().length,
        isVideoEnabled
      });
      
      // Play local video (muted, so no autoplay restrictions)
      videoElement.play().catch(err => {
        console.warn('[STREAM_WINDOW] Video play failed', err);
      });
    } else if (!isLocal) {
      // Get remote stream for this peer
      const remoteStream = session.remoteStreams.get(peerAddress);
      if (remoteStream) {
        // AV FIX 2c: Detect video tracks in remote stream and update state as fallback.
        // This catches edge cases where ontrack/av-remote-video-toggled events arrive out of order.
        // AV FIX 16: Only use track detection to UPGRADE to true when the signaling layer
        // hasn't explicitly disabled video.  With AV FIX 13 (direction='inactive' instead
        // of transceiver.stop()), receiver tracks persist with readyState='live' even after
        // the sender toggles video off, so raw track presence is no longer reliable.
        // session.remoteVideoEnabled is the authoritative source set by AV_STREAM_UPDATE.
        const signalingVideoState = session.remoteVideoEnabled?.get(peerAddress);
        const remoteVideoTracks = remoteStream.getVideoTracks().filter(t => t.readyState === 'live');
        if (remoteVideoTracks.length > 0 && !remoteVideoEnabled && signalingVideoState !== false) {
          console.log('[STREAM_WINDOW] Detected live video tracks in remote stream — updating remoteVideoEnabled', {
            peer: peerAddress.slice(0, 8),
            videoTrackCount: remoteVideoTracks.length,
            signalingState: signalingVideoState
          });
          setRemoteVideoEnabled(true);
        }
        
        // AV FIX 14: Only re-set srcObject when the stream reference has changed.
        // Previously (AV FIX B) we always re-assigned srcObject to recover from
        // ICE blips, but this causes the browser to abort any in-progress play()
        // with AbortError on every React re-render, producing flakey video.
        // Calling play() alone is sufficient to resume paused playback after ICE
        // recovery; new tracks added to the same MediaStream are picked up
        // automatically by the video element without srcObject reassignment.
        if (videoElement.srcObject !== remoteStream) {
          videoElement.srcObject = remoteStream;
        }
        
        console.log('[STREAM_WINDOW] Attached remote video stream', {
          peer: peerAddress.slice(0, 8),
          trackCount: remoteStream.getTracks().length,
          audioTracks: remoteStream.getAudioTracks().length,
          videoTracks: remoteStream.getVideoTracks().length,
          liveTracks: remoteStream.getTracks().filter(t => t.readyState === 'live').length
        });
        
        // CRITICAL: Explicitly play remote video/audio with retry for interrupted plays
        // Multiple rapid track events can interrupt play() calls, so we retry
        const attemptPlay = (retryCount: number = 0) => {
          // AV FIX 15: Skip play() if the element is already playing.
          // The attachment useEffect re-fires on isVideoEnabled/remoteVideoEnabled
          // changes, each calling play() which aborts the previous pending promise
          // with AbortError, producing cascading "Play interrupted" retries that
          // exhaust all attempts and leave video paused.
          if (!videoElement.paused) return;
          videoElement.play().catch(err => {
            // Handle "interrupted by new load request" error by retrying after brief delay
            if (err.name === 'AbortError' && retryCount < 3) {
              console.log('[STREAM_WINDOW] Play interrupted, retrying...', {
                peer: peerAddress.slice(0, 8),
                retry: retryCount + 1
              });
              setTimeout(() => attemptPlay(retryCount + 1), 100);
            } else if (err.name !== 'AbortError') {
              console.error('[STREAM_WINDOW] Remote video play failed - user interaction may be required', {
                peer: peerAddress.slice(0, 8),
                error: err.message
              });
            }
          });
        };
        attemptPlay();
        
        // Listen for track unmute events - tracks may arrive muted and unmute later
        remoteStream.getTracks().forEach(track => {
          if (track.muted) {
            const handleUnmute = () => {
              console.log('[STREAM_WINDOW] Track unmuted, attempting play', {
                peer: peerAddress.slice(0, 8),
                kind: track.kind
              });
              attemptPlay();
              track.removeEventListener('unmute', handleUnmute);
            };
            track.addEventListener('unmute', handleUnmute);
          }
        });
      }
    }
  }, [session, peerAddress, isLocal, remoteStreamUpdate, isVideoEnabled, isScreenSharing, remoteVideoEnabled]);
  
  // Attach LOCAL media stream to audio element (for visualization only)
  // Remote audio playback is handled by PersistentAudioLayer at app root level
  useEffect(() => {
    if (!audioRef.current || !isLocal) return;
    
    const audioElement = audioRef.current;
    
    if (session.localStream) {
      // Local audio for visualization only - always muted to avoid feedback
      audioElement.srcObject = session.localStream;
      audioElement.muted = true;
    }
  }, [session.localStream, isLocal]);
  
  // Determine media types
  // For local stream, use isVideoEnabled state
  // For remote stream, use remoteVideoEnabled state (updated via signaling events)
  const hasVideo = isLocal ? (isVideoEnabled || isScreenSharing) : remoteVideoEnabled;
  const hasAudio = true; // Audio is always present in A/V calls
  
  // Audio frequency monitoring using Web Audio API (8-bar visualization)
  useEffect(() => {
    if (!hasAudio || !session.localStream) return;
    
    try {
      // Create audio context and analyser
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 64; // Smaller FFT for 8 bars
      analyser.smoothingTimeConstant = 0.7;
      
      const source = audioContext.createMediaStreamSource(session.localStream);
      source.connect(analyser);
      
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      
      // Monitor frequency data for 8-bar visualization
      const updateFrequencyData = () => {
        if (!analyserRef.current) return;
        
        analyserRef.current.getByteFrequencyData(dataArray);
        
        // Map frequency bins to 8 bars with voice-optimized distribution
        // Voice frequencies: 300-3000Hz, so weight toward mid-range
        const bars: number[] = [];
        const totalBins = dataArray.length;
        
        // Logarithmic-style bin grouping for better voice visualization
        const binRanges = [
          [0, Math.floor(totalBins * 0.08)],      // Bar 1: Low bass
          [Math.floor(totalBins * 0.08), Math.floor(totalBins * 0.18)],  // Bar 2: Bass-mid
          [Math.floor(totalBins * 0.18), Math.floor(totalBins * 0.32)],  // Bar 3: Low-mid (voice)
          [Math.floor(totalBins * 0.32), Math.floor(totalBins * 0.48)],  // Bar 4: Mid (voice)
          [Math.floor(totalBins * 0.48), Math.floor(totalBins * 0.64)],  // Bar 5: High-mid (voice)
          [Math.floor(totalBins * 0.64), Math.floor(totalBins * 0.76)],  // Bar 6: Upper-mid
          [Math.floor(totalBins * 0.76), Math.floor(totalBins * 0.88)],  // Bar 7: Treble
          [Math.floor(totalBins * 0.88), totalBins]                       // Bar 8: High treble
        ];
        
        for (let i = 0; i < 8; i++) {
          const [start, end] = binRanges[i];
          const slice = dataArray.slice(start, end);
          const average = slice.reduce((sum, val) => sum + val, 0) / slice.length;
          
          // Apply frequency-specific boost for better visualization
          let boost = 1.5; // Base boost
          if (i >= 2 && i <= 5) boost = 1.8; // Extra boost for voice range (bars 3-6)
          if (i >= 6) boost = 2.2; // Stronger boost for higher frequencies
          
          bars.push(Math.min(100, (average / 255) * 100 * boost));
        }
        
        setFrequencyData(bars);
        animationFrameRef.current = requestAnimationFrame(updateFrequencyData);
      };
      
      updateFrequencyData();
      
      console.log('[STREAM_WINDOW] Audio frequency monitoring started');
    } catch (error) {
      console.error('[STREAM_WINDOW] Failed to setup audio monitoring', error);
    }
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [session.localStream, hasAudio]);
  
  // Sync isMicMuted with actual track state when tracks change (e.g. after replaceTrack)
  useEffect(() => {
    if (!isLocal || !session.localStream) return;
    const audioTrack = session.localStream.getAudioTracks()[0];
    if (audioTrack && isMicMuted !== !audioTrack.enabled) {
      setIsMicMuted(!audioTrack.enabled);
      console.log('[STREAM_WINDOW] Synced mic mute state with track', { enabled: audioTrack.enabled });
    }
  }, [session.localStream, isLocal]);

  // Toggle microphone mute/unmute (local stream)
  const toggleMicMute = () => {
    if (!session.localStream) return;
    
    const audioTracks = session.localStream.getAudioTracks();
    if (audioTracks.length === 0) return;
    
    const newEnabled = !audioTracks[0].enabled;
    audioTracks.forEach(track => {
      track.enabled = newEnabled;
    });
    
    setIsMicMuted(!newEnabled);
    
    console.log('[STREAM_WINDOW] Microphone', newEnabled ? 'unmuted' : 'muted');
  };
  
  // Toggle speaker mute/unmute (remote stream)
  // Dispatches event to PersistentAudioLayer which manages actual audio playback
  const toggleSpeakerMute = () => {
    const newMutedState = !isSpeakerMuted;
    setIsSpeakerMuted(newMutedState);
    
    // Dispatch event to PersistentAudioLayer to mute/unmute the specific peer's audio
    window.dispatchEvent(new CustomEvent('av-speaker-mute-toggle', {
      detail: {
        sessionId: session.sessionId,
        peerAddress,
        muted: newMutedState
      }
    }));
    
    console.log('[STREAM_WINDOW] Speaker', newMutedState ? 'muted' : 'unmuted', {
      peer: peerAddress.slice(0, 8)
    });
  };
  
  // Toggle video on/off (local stream only)
  const toggleVideoEnabled = async () => {
    if (!onToggleVideo || isTogglingVideo) return;
    
    setIsTogglingVideo(true);
    try {
      const newState = !isVideoEnabled;
      const success = await onToggleVideo(newState);
      if (success) {
        setIsVideoEnabled(newState);
        console.log('[STREAM_WINDOW] Video', newState ? 'enabled' : 'disabled');
      } else {
        console.warn('[STREAM_WINDOW] Failed to toggle video');
      }
    } catch (error) {
      console.error('[STREAM_WINDOW] Error toggling video', error);
    } finally {
      setIsTogglingVideo(false);
    }
  };
  
  // Listen for video toggle events from other sources
  useEffect(() => {
    const handleVideoToggled = (event: CustomEvent) => {
      if (event.detail.sessionId === session.sessionId) {
        setIsVideoEnabled(event.detail.enabled);
      }
    };
    
    window.addEventListener('av-video-toggled', handleVideoToggled as EventListener);
    return () => {
      window.removeEventListener('av-video-toggled', handleVideoToggled as EventListener);
    };
  }, [session.sessionId]);
  
  // Toggle screen share on/off (local stream only)
  const toggleScreenShareEnabled = async () => {
    if (!onToggleScreenShare || isTogglingScreenShare) return;
    
    setIsTogglingScreenShare(true);
    try {
      const newState = !isScreenSharing;
      const success = await onToggleScreenShare(newState);
      if (success) {
        setIsScreenSharing(newState);
        // If screen share enabled, camera video gets disabled by provider
        if (newState) setIsVideoEnabled(false);
        console.log('[STREAM_WINDOW] Screen share', newState ? 'enabled' : 'disabled');
      } else {
        console.warn('[STREAM_WINDOW] Failed to toggle screen share');
      }
    } catch (error) {
      console.error('[STREAM_WINDOW] Error toggling screen share', error);
    } finally {
      setIsTogglingScreenShare(false);
    }
  };
  
  // Listen for screen share toggle events from other sources (e.g. browser stop button)
  useEffect(() => {
    const handleScreenShareToggled = (event: CustomEvent) => {
      if (event.detail.sessionId === session.sessionId) {
        setIsScreenSharing(event.detail.enabled);
      }
    };
    
    window.addEventListener('av-screen-share-toggled', handleScreenShareToggled as EventListener);
    return () => {
      window.removeEventListener('av-screen-share-toggled', handleScreenShareToggled as EventListener);
    };
  }, [session.sessionId]);
  
  // Monitor remote audio level for green outline indicator
  useEffect(() => {
    if (isLocal) return;
    const remoteStream = session.remoteStreams.get(peerAddress);
    if (!remoteStream) return;
    const audioTracks = remoteStream.getAudioTracks().filter(t => t.readyState === 'live');
    if (audioTracks.length === 0) return;

    try {
      const ctx = new AudioContext();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.5;
      const source = ctx.createMediaStreamSource(remoteStream);
      source.connect(analyser);

      remoteAudioCtxRef.current = ctx;
      remoteAnalyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const poll = () => {
        if (!remoteAnalyserRef.current) return;
        remoteAnalyserRef.current.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((s, v) => s + v, 0) / dataArray.length;
        setRemoteAudioActive(avg > 3);
        remoteAnimFrameRef.current = requestAnimationFrame(poll);
      };
      poll();
    } catch (err) {
      console.warn('[STREAM_WINDOW] Failed to setup remote audio monitor', err);
    }

    return () => {
      if (remoteAnimFrameRef.current) cancelAnimationFrame(remoteAnimFrameRef.current);
      if (remoteAudioCtxRef.current) remoteAudioCtxRef.current.close();
      remoteAnalyserRef.current = null;
    };
  }, [isLocal, peerAddress, session.remoteStreams, remoteStreamUpdate]);

  // Derive local audio activity from existing frequency data
  const localAudioActive = isLocal && frequencyData.some(v => v > 5);

  const isActive = session.state === 'active';
  const isConnecting = session.state === 'connecting';
  const isRinging = session.state === 'ringing';
  const isAudioActive = isLocal ? localAudioActive : remoteAudioActive;
  
  return (
    <div className={`relative bg-surface-container rounded-lg overflow-hidden aspect-video transition-shadow ${isAudioActive ? 'ring-2 ring-green-500' : ''}`}>
      {/* Audio element - ONLY for local stream visualization, NOT for playback */}
      {/* Remote audio playback is handled by PersistentAudioLayer at app root level */}
      {/* This ensures audio continues during tab navigation */}
      {hasAudio && !hasVideo && isLocal && (
        <audio
          ref={audioRef}
          autoPlay
          playsInline
          muted={true}
        />
      )}
      
      {/* Video element - always rendered so srcObject can be set before remoteVideoEnabled state catches up.
         Hidden when no video to show avatar fallback. AV FIX 2b. */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={isLocal}
        className={`w-full h-full object-cover ${hasVideo ? '' : 'hidden'}`}
      />
      
      {/* Avatar fallback for audio-only calls */}
      {!hasVideo && (
        <div 
          className="w-full h-full flex flex-col items-center justify-center relative overflow-hidden"
          style={{ backgroundColor: isLocal ? userPreferences.bannerColor : getPeerBannerColor(contactProfile) }}
        >
          {/* Avatar Circle - Uses avatar color for separation from banner */}
          <div 
            className="relative rounded-full w-24 h-24 flex items-center justify-center text-white z-10"
            style={{ backgroundColor: isLocal ? userPreferences.avatarColor : getPeerAvatarColor(contactProfile) }}
          >
            <span className="text-4xl font-medium">{contactInitials}</span>
            {renderPeerAvatarEmoji(isLocal ? { avatarEmoji: userPreferences.avatarEmoji } as any : contactProfile, "lg")}
          </div>
          
          {/* 8-Bar Waveform Visualization (Bottom) */}
          {isLocal && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-end gap-1 h-12">
              {frequencyData.map((value, i) => {
                // Create gradient from banner color (base) to avatar color (top) with diffused edge
                const bannerColor = userPreferences.bannerColor || '#6200EE';
                const avatarColor = userPreferences.avatarColor || '#03DAC6';
                
                return (
                  <div
                    key={i}
                    className="w-2 rounded-t-sm transition-all duration-75"
                    style={{
                      height: `${Math.max(4, (value / 100) * 48)}px`,
                      background: `linear-gradient(to top, ${bannerColor}, ${avatarColor}cc)`, // cc = 80% opacity for diffusion
                      filter: 'blur(0.5px)' // Subtle blur for smooth diffused edge
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
      
      {/* Status overlay */}
      <div className="absolute top-2 left-2 right-2 flex items-center justify-between">
        <div className="bg-surface-container-highest/80 backdrop-blur-sm text-on-surface px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
          {isGroupInitiator && <CrownIcon size={12} className="text-tertiary" />}
          {isLocal ? "You" : contactName}
        </div>
        
        <div className="flex gap-1">
          {/* Camera Toggle (local stream only, active calls only) */}
          {isLocal && isActive && onToggleVideo && (
            <button
              onClick={toggleVideoEnabled}
              disabled={isTogglingVideo}
              className={`backdrop-blur-sm p-1.5 rounded-full transition-colors ${
                isVideoEnabled 
                  ? 'bg-primary/80 hover:bg-primary text-on-primary' 
                  : 'bg-surface-container-highest/80 hover:bg-surface-container-highest text-on-surface'
              } ${isTogglingVideo ? 'opacity-50 cursor-wait' : ''}`}
              title={isVideoEnabled ? "Turn Off Camera" : "Turn On Camera"}
            >
              {isVideoEnabled ? <VideoIcon size={16} /> : <VideoOffIcon size={16} />}
            </button>
          )}
          
          {/* Screen Share Toggle (local stream only, active calls only) */}
          {isLocal && isActive && onToggleScreenShare && (
            <button
              onClick={toggleScreenShareEnabled}
              disabled={isTogglingScreenShare}
              className={`backdrop-blur-sm p-1.5 rounded-full transition-colors ${
                isScreenSharing 
                  ? 'bg-tertiary/80 hover:bg-tertiary text-on-tertiary' 
                  : 'bg-surface-container-highest/80 hover:bg-surface-container-highest text-on-surface'
              } ${isTogglingScreenShare ? 'opacity-50 cursor-wait' : ''}`}
              title={isScreenSharing ? "Stop Screen Share" : "Share Screen"}
            >
              <MonitorUp size={16} />
            </button>
          )}
          
          {/* Microphone Mute/Unmute (local stream only) */}
          {hasAudio && isLocal && (
            <button
              onClick={toggleMicMute}
              className={`backdrop-blur-sm p-1.5 rounded-full transition-colors ${
                isMicMuted 
                  ? 'bg-error/80 hover:bg-error text-on-error' 
                  : 'bg-surface-container-highest/80 hover:bg-surface-container-highest text-on-surface'
              }`}
              title={isMicMuted ? "Unmute Microphone" : "Mute Microphone"}
            >
              {isMicMuted ? <MicOffIcon size={16} /> : <MicIcon size={16} />}
            </button>
          )}
          
          {/* Speaker Mute/Unmute (remote stream only) */}
          {hasAudio && !isLocal && (
            <button
              onClick={toggleSpeakerMute}
              className={`backdrop-blur-sm p-1.5 rounded-full transition-colors ${
                isSpeakerMuted 
                  ? 'bg-error/80 hover:bg-error text-on-error' 
                  : 'bg-surface-container-highest/80 hover:bg-surface-container-highest text-on-surface'
              }`}
              title={isSpeakerMuted ? "Unmute Speaker" : "Mute Speaker"}
            >
              {isSpeakerMuted ? <MicOffIcon size={16} /> : <Volume2Icon size={16} />}
            </button>
          )}
          
          {/* Video indicator (shows when remote has video enabled) */}
          {hasVideo && !isLocal && (
            <div className="bg-surface-container-highest/80 backdrop-blur-sm p-1.5 rounded-full">
              <VideoIcon size={16} className="text-on-surface" />
            </div>
          )}
        </div>
      </div>
      
      {/* Connection status overlay — only for ringing/connecting states.
          GRAY SCREEN FIX: Previously used !isActive which also matched 'ended' state,
          causing a blank gray overlay during the async React unmount window. */}
      {(isRinging || isConnecting) && !isReconnecting && (
        <div className="absolute inset-0 bg-surface/60 backdrop-blur-sm flex items-center justify-center">
          <div className="text-center">
            {isRinging && (
              <div className="text-on-surface text-lg font-medium">
                {isLocal ? "Calling..." : "Incoming call"}
              </div>
            )}
            {isConnecting && (
              <div className="text-on-surface text-lg font-medium">
                Connecting...
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Reconnecting overlay */}
      {isReconnecting && (
        <div className="absolute inset-0 bg-surface/70 backdrop-blur-sm flex items-center justify-center">
          <div className="text-center">
            <div className="text-on-surface text-lg font-medium animate-pulse">
              Reconnecting...
            </div>
            <div className="text-on-surface-variant text-sm mt-1">
              Connection lost, attempting to restore
            </div>
          </div>
        </div>
      )}
      
      {/* End call button (only show on local stream or when we can control it) */}
      {isLocal && onEndCall && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
          <button
            onClick={onEndCall}
            className="bg-error text-on-error hover:bg-error/80 p-3 rounded-full transition-colors"
            title="End call"
          >
            <PhoneOffIcon size={20} />
          </button>
        </div>
      )}
      
      {/* Audio quality/enhancement indicator */}
      {hasAudio && !hasVideo && (
        <div className="absolute bottom-2 right-2 text-xs">
          <div className="bg-surface-container-highest/80 backdrop-blur-sm text-on-surface px-2 py-1 rounded-full flex items-center gap-1">
            <Volume2Icon size={12} />
            <span>
              {session.quality === 0 ? "Low" :
               session.quality === 1 ? "Med" :
               session.quality === 2 ? "High" :
               "Auto"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
